// src/modules/gyms/components/ServiceFormModal.tsx
import { Modal } from "react-bootstrap";
import {
  FormEvent,
  useState,
  useEffect,
  useRef,
  useContext,
  useMemo,
} from "react";
import {
  GymService,
  GymServiceImage,
  GYM_SERVICE_CATEGORIES,
  GYM_SERVICE_UNIT_TYPES,
} from "../models/gym.model";
import { message } from "antd";
import { gymPartnerService } from "../services/gym.service";
import { AuthContext } from "@modules/auth/contexts/auth-plus.context";
import { hasManagePermission } from "@modules/auth/utils/role.utils";
import { formatApiDateTime } from "@modules/shared/utils/handle-date.utils";
import {
  previewExpirationAt,
  toDateInputValue,
} from "../utils/service-expiration";

interface ServiceFormModalProps {
  show: boolean;
  brandId: string;
  branchOptions: { id: string; name: string }[];
  editingService?: GymService | null;
  defaultBranchId?: string; // 👈 thêm prop này
  startInViewMode?: boolean;
  onSave: (
    data: Omit<GymService, "id" | "createdAt" | "updatedAt">,
  ) => Promise<void>;
  onClose: () => void;
}

type ServiceForm = Omit<
  GymService,
  "id" | "createdAt" | "updatedAt" | "price"
> & {
  price: number | null;
};

const UNIT_LABELS: Record<string, string> = {
  SESSION: "Buổi (SESSION)",
  DAY: "Ngày (DAY)",
  MONTH: "Tháng (MONTH)",
};

const formatPricePreview = (price: number) =>
  new Intl.NumberFormat("en-US").format(price);

const emptyForm = (brandId: string): ServiceForm => ({
  brandId,
  branchId: "",
  name: "",
  category: "GYM",
  price: null,
  amount: 1,
  unitType: "MONTH",
  description: "",
  active: true,
  images: [],
  expirationAt: null,
});

const todayInput = () => toDateInputValue(new Date());

export const ServiceFormModal = ({
  show,
  brandId,
  branchOptions,
  editingService,
  defaultBranchId,
  startInViewMode = false,
  onSave,
  onClose,
}: ServiceFormModalProps) => {
  const { auth, setAuth, logout } = useContext(AuthContext);
  const authArgs = { auth, setAuth, logout };
  const canManage = hasManagePermission(auth);

  const [form, setForm] = useState(emptyForm(brandId));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [editMode, setEditMode] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const MAX_IMAGE_SIZE_MB = 2;
  const MAX_IMAGE_SIZE_BYTES = MAX_IMAGE_SIZE_MB * 1024 * 1024;
  const isExisting = !!editingService;
  const readOnly = isExisting && !editMode;

  // Kiểm tra xem branch có bị fix cứng không
  const isBranchFixed = !!defaultBranchId && !isExisting;

  useEffect(() => {
    if (editingService) {
      setForm({
        brandId: editingService.brandId,
        branchId: editingService.branchId,
        name: editingService.name,
        category: editingService.category,
        price: editingService.price,
        amount: editingService.amount,
        unitType: editingService.unitType,
        description: editingService.description,
        active: editingService.active,
        images: editingService.images,
        expirationAt: editingService.expirationAt,
      });
      setEditMode(!startInViewMode);
    } else {
      const initialForm = emptyForm(brandId);
      // Nếu có defaultBranchId, gán vào form
      if (defaultBranchId) {
        initialForm.branchId = defaultBranchId;
      }
      setForm(initialForm);
      setEditMode(true);
    }
    setErrors({});
    setTouched({});
  }, [editingService, brandId, show, startInViewMode, defaultBranchId]);

  const previewExpiry = useMemo(() => {
    if (form.unitType === "SESSION") return null;
    if (!form.amount || form.amount < 1) return null;
    return previewExpirationAt(form.unitType, form.amount);
  }, [form.unitType, form.amount]);

  const validateField = (field: keyof ServiceForm, value: any): string => {
    switch (field) {
      case "name":
        if (!value?.trim()) return "Vui lòng nhập tên dịch vụ";
        if (value.trim().length < 2) return "Tên phải có ít nhất 2 ký tự";
        return "";
      case "price":
        if (value == null || value === "" || Number(value) <= 0) {
          return "Giá phải lớn hơn 0";
        }
        return "";
      case "amount":
        if (!value || value < 1) return "Số lượng phải ít nhất 1";
        return "";
      case "branchId":
        if (!value) return "Vui lòng chọn chi nhánh";
        return "";
      case "expirationAt":
        if (form.unitType !== "SESSION") return "";
        if (!value) return "Ngày hết hạn bắt buộc với loại Buổi (SESSION)";
        if (toDateInputValue(value) < todayInput()) {
          return "Ngày hết hạn phải từ hôm nay trở đi";
        }
        return "";
      default:
        return "";
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    (["name", "price", "amount", "branchId", "expirationAt"] as const).forEach(
      (field) => {
        const error = validateField(field, form[field]);
        if (error) newErrors[field] = error;
      },
    );
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBlur = (field: keyof ServiceForm) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, form[field]);
    setErrors((prev) => {
      if (error) return { ...prev, [field]: error };
      const { [field]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (readOnly) return;
    if (!canManage) {
      message.error("Bạn không có quyền quản lý dịch vụ.");
      return;
    }
    setTouched({
      name: true,
      price: true,
      amount: true,
      branchId: true,
      expirationAt: true,
      images: true,
    });
    if (!validateForm()) {
      message.error("Vui lòng điền đủ các trường bắt buộc");
      return;
    }
    setSaving(true);
    try {
      await onSave({
        ...form,
        price: Number(form.price),
      });
      onClose();
    } catch (err: any) {
      message.error(err.message || "Lưu dịch vụ thất bại");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof ServiceForm, value: any) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "unitType" && value !== "SESSION") {
        next.expirationAt = null;
      }
      return next;
    });
    setErrors((prev) => {
      const { [field]: _, ...rest } = prev;
      return rest;
    });
  };

  const uploadImage = async (
    file: File,
  ): Promise<{ success: boolean; data?: GymServiceImage; error?: string }> => {
    try {
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        return {
          success: false,
          error: `${file.name} vượt quá ${MAX_IMAGE_SIZE_MB}MB`,
        };
      }
      const result = await gymPartnerService.uploadMedia(file, authArgs);
      return { success: true, data: { url: result.url, key: result.key } };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || `Upload thất bại: ${file.name}`,
      };
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || readOnly) return;
    setUploading(true);
    try {
      const results = await Promise.all(
        Array.from(files).map((file) => uploadImage(file)),
      );
      const added = results
        .filter((r) => r.success && r.data)
        .map((r) => r.data!) as GymServiceImage[];
      const failed = results.filter((r) => !r.success);
      if (failed.length) {
        message.warning(`${failed.length} ảnh upload thất bại`);
      }
      if (added.length) {
        setForm((prev) => ({ ...prev, images: [...prev.images, ...added] }));
        setErrors((prev) => {
          const { images, ...rest } = prev;
          return rest;
        });
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = (index: number) => {
    if (readOnly) return;
    setForm((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const resetFromEditing = () => {
    if (!editingService) return;
    setForm({
      brandId: editingService.brandId,
      branchId: editingService.branchId,
      name: editingService.name,
      category: editingService.category,
      price: editingService.price,
      amount: editingService.amount,
      unitType: editingService.unitType,
      description: editingService.description,
      active: editingService.active,
      images: editingService.images,
      expirationAt: editingService.expirationAt,
    });
    setErrors({});
    setTouched({});
  };

  const getFieldError = (field: string) =>
    touched[field] ? errors[field] || "" : "";
  const inputClass = (field: string) =>
    `form-control ${touched[field] && errors[field] ? "is-invalid" : ""}`;

  // Xác định branch select có bị disabled không
  const isBranchDisabled = readOnly || isBranchFixed;

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bold d-flex align-items-center gap-2">
          {isExisting
            ? editMode
              ? "Sửa dịch vụ"
              : "Chi tiết dịch vụ"
            : "Thêm dịch vụ"}
          {isExisting && canManage && (
            <button
              type="button"
              className={`btn btn-sm ${editMode ? "btn-outline-secondary" : "btn-outline-primary"}`}
              onClick={() => {
                if (editMode) resetFromEditing();
                setEditMode((v) => !v);
              }}
            >
              {editMode ? "Hủy sửa" : "Sửa"}
            </button>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4">
        <form onSubmit={handleSubmit}>
          <fieldset disabled={readOnly}>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  Tên dịch vụ <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={inputClass("name")}
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  onBlur={() => handleBlur("name")}
                  placeholder="VD: Gói tập 1 tháng"
                />
                {getFieldError("name") && (
                  <div className="invalid-feedback d-block">
                    {getFieldError("name")}
                  </div>
                )}
              </div>

              <div className="col-md-3">
                <label className="form-label fw-semibold">Danh mục</label>
                <select
                  className="form-select"
                  value={form.category}
                  onChange={(e) => handleChange("category", e.target.value)}
                >
                  {GYM_SERVICE_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label fw-semibold">Trạng thái</label>
                <select
                  className="form-select"
                  value={form.active ? "ACTIVE" : "INACTIVE"}
                  onChange={(e) =>
                    handleChange("active", e.target.value === "ACTIVE")
                  }
                >
                  <option value="ACTIVE">Đang bán</option>
                  <option value="INACTIVE">Tạm tắt</option>
                </select>
              </div>

              <div className="col-md-4">
                <label className="form-label fw-semibold">
                  Giá (VND) <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className={inputClass("price")}
                  value={form.price ?? ""}
                  onChange={(e) => {
                    const raw = e.target.value;
                    handleChange("price", raw === "" ? null : Number(raw));
                  }}
                  onBlur={() => handleBlur("price")}
                  min={0}
                  step={1000}
                  placeholder="VD: 10000000"
                />
                {form.price != null && form.price > 0 && (
                  <div className="form-text mb-0">
                    {formatPricePreview(form.price)} VND
                  </div>
                )}
                {getFieldError("price") && (
                  <div className="invalid-feedback d-block">
                    {getFieldError("price")}
                  </div>
                )}
              </div>

              <div className="col-md-4">
                <label className="form-label fw-semibold">
                  Số lượng <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className={inputClass("amount")}
                  value={form.amount}
                  onChange={(e) =>
                    handleChange("amount", Number(e.target.value))
                  }
                  onBlur={() => handleBlur("amount")}
                  min={1}
                />
                {getFieldError("amount") && (
                  <div className="invalid-feedback d-block">
                    {getFieldError("amount")}
                  </div>
                )}
              </div>

              <div className="col-md-4">
                <label className="form-label fw-semibold">Đơn vị</label>
                <select
                  className="form-select"
                  value={form.unitType}
                  onChange={(e) => handleChange("unitType", e.target.value)}
                >
                  {GYM_SERVICE_UNIT_TYPES.map((u) => (
                    <option key={u} value={u}>
                      {UNIT_LABELS[u] || u}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  Chi nhánh <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${touched.branchId && errors.branchId ? "is-invalid" : ""}`}
                  value={form.branchId}
                  onChange={(e) => handleChange("branchId", e.target.value)}
                  onBlur={() => handleBlur("branchId")}
                  disabled={isBranchDisabled}
                >
                  <option value="">Chọn chi nhánh</option>
                  {branchOptions.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
                {isBranchFixed && <div className="form-text text-muted"></div>}
                {getFieldError("branchId") && (
                  <div className="invalid-feedback d-block">
                    {getFieldError("branchId")}
                  </div>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold">
                  Ngày hết hạn
                  {form.unitType === "SESSION" && (
                    <span className="text-danger"> *</span>
                  )}
                </label>
                {form.unitType === "SESSION" ? (
                  <>
                    <input
                      type="date"
                      className={inputClass("expirationAt")}
                      value={toDateInputValue(form.expirationAt)}
                      min={todayInput()}
                      onChange={(e) =>
                        handleChange(
                          "expirationAt",
                          e.target.value
                            ? new Date(e.target.value).toISOString()
                            : null,
                        )
                      }
                      onBlur={() => handleBlur("expirationAt")}
                    />
                    {getFieldError("expirationAt") && (
                      <div className="invalid-feedback d-block">
                        {getFieldError("expirationAt")}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="form-control-plaintext text-muted small border rounded px-3 py-2 bg-light">
                    {isExisting && !editMode && form.expirationAt
                      ? formatApiDateTime(form.expirationAt)
                      : previewExpiry
                        ? `Hết hạn ~ ${formatApiDateTime(previewExpiry.toISOString())} (tự tính)`
                        : "Nhập số lượng để xem ngày hết hạn dự kiến"}
                    <div className="form-text mb-0">
                      Hệ thống tự tính theo số lượng × đơn vị (1 tháng = 30
                      ngày)
                    </div>
                  </div>
                )}
              </div>

              <div className="col-12">
                <label className="form-label fw-semibold">Mô tả</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={form.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  maxLength={2000}
                  placeholder="Mô tả dịch vụ..."
                />
                <div className="form-text text-end">
                  {form.description.length}/2000
                </div>
              </div>

              <div className="col-12">
                <label className="form-label fw-semibold">
                  Ảnh{" "}
                  <span className="text-muted fw-normal">(không bắt buộc)</span>
                </label>
                {!readOnly && (
                  <input
                    ref={fileInputRef}
                    type="file"
                    className={`form-control ${touched.images && errors.images ? "is-invalid" : ""}`}
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    disabled={uploading}
                  />
                )}
                {getFieldError("images") && (
                  <div className="invalid-feedback d-block">
                    {getFieldError("images")}
                  </div>
                )}
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {form.images.map((img, idx) => (
                    <div
                      key={`${img.key}-${idx}`}
                      className="position-relative"
                      style={{ width: 100, height: 80 }}
                    >
                      <img
                        src={img.url}
                        alt={`service-${idx}`}
                        className="rounded border"
                        loading="lazy"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      {!readOnly && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger position-absolute top-0 end-0 rounded-circle p-0"
                          style={{ width: 20, height: 20, fontSize: 12 }}
                          onClick={() => removeImage(idx)}
                          disabled={uploading}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  {form.images.length === 0 && (
                    <div className="text-muted small">Chưa có ảnh</div>
                  )}
                </div>
              </div>
            </div>
          </fieldset>

          <div className="text-end mt-4 pt-3 border-top">
            <button
              type="button"
              className="btn btn-light me-2 px-4"
              onClick={onClose}
              disabled={saving || uploading}
            >
              {readOnly ? "Đóng" : "Hủy"}
            </button>
            {!readOnly && (
              <button
                type="submit"
                className="btn btn-primary px-4"
                disabled={saving || uploading}
              >
                {saving
                  ? "Đang lưu..."
                  : isExisting
                    ? "Cập nhật"
                    : "Tạo dịch vụ"}
              </button>
            )}
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
